import clsx from 'clsx';
import React from 'react';

export interface TranscriptLine {
  speaker: 'arthur' | 'guest' | 'note';
  text: string;
}

const TRANSCRIPT_NOISE =
  /^(call summary|recent dialogue|tool events|node transitions|order attempt detail)\b/i;
const TOOL_LINE = /^tool (call|result)\b/i;

/**
 * Turns a stored transcript into speaker turns.
 *
 * Lines arrive prefixed with a role ("Agent:", "Caller:", "Customer:").
 * Anything without a recognised prefix is kept as a note. Tool-call digest
 * lines are dropped so the script matches the voicemail transcript. Retell
 * emits one line per utterance fragment ("Agent: This", "Agent: call may
 * be…"), so consecutive fragments from the same speaker are merged back
 * into a single turn.
 */
export function parseTranscript(transcript?: string | null): TranscriptLine[] {
  if (!transcript) return [];
  const lines = transcript
    .split('\n')
    .map((l) => l.trim())
    .filter(
      (line) => line && !TRANSCRIPT_NOISE.test(line) && !TOOL_LINE.test(line),
    )
    .map((line): TranscriptLine => {
      const m = line.match(
        /^(Agent|Arthur|User|Caller|Customer|Guest):\s*(.*)$/i,
      );
      if (!m) return { speaker: 'note', text: line };
      const role = m[1].toLowerCase();
      return {
        speaker: role === 'agent' || role === 'arthur' ? 'arthur' : 'guest',
        text: m[2].trim(),
      };
    })
    .filter((l) => l.text);
  const merged: TranscriptLine[] = [];
  for (const l of lines) {
    const prev = merged[merged.length - 1];
    if (prev && prev.speaker === l.speaker && l.speaker !== 'note') {
      prev.text = `${prev.text} ${l.text}`.replace(/\s+/g, ' ');
    } else {
      merged.push({ ...l });
    }
  }
  return merged;
}

/**
 * Transcript as a readable script, not chat bubbles.
 * Speaker in a fixed 72px gutter, text runs to a ~68ch measure. Arthur's
 * turns sit on a faint accent wash so the two voices alternate visibly when
 * scanning; the guest's are plain. Notes (lines without a speaker) span the
 * full width, muted.
 */
export const TranscriptView: React.FC<{
  transcript?: string | null;
  /** Pre-parsed lines, when the caller already has them. */
  lines?: TranscriptLine[];
  guestLabel?: string;
  agentLabel?: string;
  maxHeight?: number;
  className?: string;
}> = ({
  transcript,
  lines,
  guestLabel = 'Guest',
  agentLabel = 'Arthur',
  maxHeight,
  className,
}) => {
  const parsed = lines ?? parseTranscript(transcript);
  if (parsed.length === 0) {
    return (
      <p className={clsx('text-v2-12 text-v2-fg-3', className)}>
        Transcript not available.
      </p>
    );
  }
  return (
    <ol
      className={clsx(
        'v2-scroll flex flex-col gap-1',
        maxHeight && 'overflow-y-auto pr-2',
        className,
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {parsed.map((l, i) =>
        l.speaker === 'note' ? (
          <li
            key={i}
            className="px-3 py-1.5 text-v2-12 text-v2-fg-3 [overflow-wrap:anywhere]"
          >
            <span className="sr-only">Note: </span>
            <span className="whitespace-pre-wrap">{l.text}</span>
          </li>
        ) : (
          <li
            key={i}
            className={clsx(
              'grid grid-cols-[72px_minmax(0,1fr)] gap-3 rounded-v2 px-3 py-2',
              l.speaker === 'arthur' && 'bg-v2-accent/[0.07]',
            )}
          >
            <span
              className={clsx(
                'truncate text-v2-12 font-semibold leading-[22px]',
                l.speaker === 'arthur' ? 'text-v2-accent' : 'text-v2-fg-2',
              )}
            >
              {l.speaker === 'arthur' ? agentLabel : guestLabel}
            </span>
            <p className="max-w-[68ch] whitespace-pre-wrap text-v2-14 leading-[22px] text-v2-fg [overflow-wrap:anywhere]">
              {l.text}
            </p>
          </li>
        ),
      )}
    </ol>
  );
};
